import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, RefreshCw, LogOut, ToggleLeft, ToggleRight } from "lucide-react";

const STATUSES = [
  { key: "new", label: "New", color: "bg-gold text-black" },
  { key: "preparing", label: "Preparing", color: "bg-orange-500 text-black" },
  { key: "out_for_delivery", label: "Out for Delivery", color: "bg-blue-500 text-white" },
  { key: "delivered", label: "Delivered", color: "bg-green-600 text-white" },
  { key: "cancelled", label: "Cancelled", color: "bg-zinc-700 text-white" },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [storeMode, setStoreMode] = useState(null);
  const [modeLoading, setModeLoading] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data.orders || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("lsk_admin_token");
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  const loadMode = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/store/mode");
      setStoreMode(data.mode);
    } catch {}
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("lsk_admin_token")) { nav("/admin/login"); return; }
    load();
    loadMode();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load, loadMode, nav]);

  const toggleMode = async () => {
    const next = storeMode === "auto" ? "closed" : "auto";
    setModeLoading(true);
    try {
      const { data } = await api.post("/admin/store/mode", { mode: next });
      setStoreMode(data.mode);
    } catch {} finally {
      setModeLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      load();
    } catch {}
  };

  const logout = () => {
    localStorage.removeItem("lsk_admin_token");
    nav("/admin/login");
  };

  const isClosed = storeMode === "closed";

  return (
    <div className="min-h-screen bg-ink text-cream">
      <header className="border-b border-border bg-black/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="font-heading text-cream uppercase tracking-tight text-2xl">
            <span className="text-gold">LSK</span> Control Room
          </div>
          <div className="flex items-center gap-2">
            {storeMode !== null && (
              <button
                onClick={toggleMode}
                disabled={modeLoading}
                title={isClosed ? "Store is manually CLOSED — click to set to Auto" : "Store is on Auto hours — click to force CLOSED"}
                className={`inline-flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-xs border-2 transition ${
                  isClosed
                    ? "border-accent text-accent hover:bg-accent hover:text-black"
                    : "border-green-500 text-green-400 hover:bg-green-500 hover:text-black"
                }`}
                data-testid="store-mode-toggle"
              >
                {modeLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isClosed ? (
                  <ToggleLeft className="h-4 w-4" />
                ) : (
                  <ToggleRight className="h-4 w-4" />
                )}
                {isClosed ? "Closed (Manual)" : "Auto Hours"}
              </button>
            )}
            <Link to="/admin/menu" className="btn-outline-poster !px-3 !py-2 !text-sm" data-testid="admin-menu-link">Menu Editor</Link>
            <button onClick={load} className="btn-outline-poster !px-3 !py-2 !text-sm" data-testid="refresh-orders-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={logout} className="btn-outline-poster !px-3 !py-2 !text-sm" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {isClosed && (
        <div className="bg-accent/20 border-b border-accent/60 text-center py-2 text-accent font-heading uppercase tracking-widest text-sm">
          Store is manually closed — customers cannot place orders
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {orders === null ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 text-cream/60 font-heading uppercase tracking-widest" data-testid="no-orders-msg">No orders yet</div>
        ) : (
          <div className="space-y-3" data-testid="admin-orders-list">
            {orders.map((o) => (
              <div key={o.id} className="poster-card p-5" data-testid={`order-row-${o.id}`}>
                <div className="grid lg:grid-cols-12 gap-4 items-start">
                  <div className="lg:col-span-3">
                    <div className="font-heading text-gold text-2xl uppercase">£{o.total_gbp.toFixed(2)}</div>
                    <div className="text-xs text-cream/50">{new Date(o.created_at).toLocaleString("en-GB")}</div>
                    <div className={`inline-block mt-2 px-2 py-0.5 text-xs font-heading uppercase tracking-widest ${
                      o.payment_status === "paid" ? "bg-green-700 text-white" :
                      o.payment_status === "cash_on_delivery" ? "bg-amber-600 text-black" :
                      "bg-zinc-700 text-white"
                    }`}>
                      {o.payment_status === "cash_on_delivery" ? "Cash on Delivery" : o.payment_status}
                    </div>
                    <div className="text-cream/40 text-[10px] font-heading uppercase tracking-widest mt-1">
                      {o.payment_method === "cash" ? "Cash" : "Card"}
                    </div>
                  </div>
                  <div className="lg:col-span-4">
                    <div className="font-heading uppercase tracking-tight text-lg text-cream">{o.delivery.name}</div>
                    <div className="text-cream/70 text-sm">{o.delivery.phone} · {o.delivery.email}</div>
                    <div className="text-cream/60 text-sm mt-1">
                      {o.delivery.address_line1}{o.delivery.address_line2 ? `, ${o.delivery.address_line2}` : ""}, {o.delivery.city} {o.delivery.postcode}
                    </div>
                    <div className="text-cream/40 text-xs mt-1">{o.delivery_area || ""}</div>
                    {o.delivery.notes && <div className="mt-2 text-cream/70 text-xs italic">"{o.delivery.notes}"</div>}
                  </div>
                  <div className="lg:col-span-3">
                    <ul className="text-sm space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="text-cream/80 flex justify-between gap-2">
                          <span>{it.quantity}× {it.name}</span>
                          <span className="text-cream/50">£{it.line_total.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:col-span-2 flex flex-col gap-1">
                    {STATUSES.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => updateStatus(o.id, s.key)}
                        className={`px-2 py-1 font-heading uppercase tracking-widest text-xs border-2 transition ${
                          o.status === s.key ? `${s.color} border-transparent` : "border-border text-cream/60 hover:border-gold hover:text-gold"
                        }`}
                        data-testid={`set-status-${o.id}-${s.key}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
